import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient, API_PRX } from '@indipia/shared';
import { Search } from '@indipia/ui-core';
import { fetchFaqs } from '../api/faqs';
import type { FaqCategory, FaqItem } from '../api/faqs';

async function fetchLandingFaqCategories(): Promise<FaqCategory[]> {
  const res = await apiClient.get(`${API_PRX.INDIPIA}/support/faqs/categories`, {
    params: { codeGroupCode: 'ZEU_FAQ_LAND_CATG' },
  });
  const list = (res.data?.data ?? []) as Record<string, unknown>[];
  return list.map((raw) => ({
    faqCategory:  (raw['faqCategory']  ?? raw['faq_category'])  as string,
    categoryName: (raw['categoryName'] ?? raw['category_name']) as string,
    faqCount:     ((raw['faqCount']    ?? raw['faq_count'] ?? 0)) as number,
  }));
}

function FaqRow({ faq, isOpen, onToggle }: { faq: FaqItem; isOpen: boolean; onToggle: () => void }) {
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-4 border-b border-border px-5 py-4 text-left transition-colors hover:bg-surface-subtle"
      >
        <span className={`flex-1 text-sm font-semibold leading-snug ${isOpen ? 'text-text' : 'text-text-muted'}`}>
          {faq.faqQuestion}
        </span>
        <svg
          className="shrink-0 text-text-muted transition-transform"
          style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}
          width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
      {isOpen && (
        <div className="border-b border-border bg-surface-subtle px-6 pb-5 pt-4">
          {faq.faqAnswer ? (
            <div
              className="faq-prose text-sm leading-relaxed text-text-muted"
              dangerouslySetInnerHTML={{ __html: faq.faqAnswer }}
            />
          ) : (
            <p className="text-sm text-text-disabled">답변이 준비되지 않았습니다.</p>
          )}
        </div>
      )}
    </div>
  );
}

export function SplashFaqPage() {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [openFaqId, setOpenFaqId] = useState<number | null>(null);

  const { data: categories = [] } = useQuery({
    queryKey: ['splash-faq-categories'],
    queryFn: fetchLandingFaqCategories,
  });

  const { data: faqs = [], isLoading } = useQuery({
    queryKey: ['splash-faqs', selectedCategory],
    queryFn: () => fetchFaqs(selectedCategory, 'ZEU_FAQ_LAND_CATG'),
  });

  function handleCategoryChange(code: string | null) {
    setSelectedCategory(code);
    setOpenFaqId(null);
    setSearchQuery('');
    setSearchInput('');
  }

  const filtered: FaqItem[] = searchQuery
    ? faqs.filter((f) => f.faqQuestion.toLowerCase().includes(searchQuery.toLowerCase()))
    : faqs;

  return (
    <div className="mx-auto w-full max-w-3xl px-6 py-10 pb-16">
      <div className="mb-6">
        <h2 className="mb-1 text-xl font-bold text-text">자주 묻는 질문</h2>
        <p className="text-xs text-text-muted">인디피아 이용 중 궁금한 점을 빠르게 찾아보세요</p>
      </div>

      {/* 검색 */}
      <div className="mb-5 flex">
        <Search
          value={searchInput}
          onChange={setSearchInput}
          onKeyDown={(e) => { if (e.key === 'Enter') { setSearchQuery(searchInput.trim()); setOpenFaqId(null); } }}
          placeholder="궁금한 점을 검색해 보세요"
        />
      </div>

      {/* 카테고리 탭 */}
      <div className="mb-0 flex gap-0.5 border-b border-border">
        {[{ code: null, name: '전체' }, ...categories.map((c) => ({ code: c.faqCategory, name: c.categoryName }))].map((tab) => (
          <button
            key={tab.code ?? 'all'}
            type="button"
            onClick={() => handleCategoryChange(tab.code)}
            className={`-mb-px rounded-none border-b-2 px-4 py-2 text-xs font-semibold transition-colors ${
              selectedCategory === tab.code ? 'border-point text-point' : 'border-transparent text-text-muted'
            }`}
          >
            {tab.name}
          </button>
        ))}
      </div>

      {/* FAQ 목록 */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <svg className="animate-spin text-text-disabled" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
            <path d="M12 2a10 10 0 0 1 10 10" />
          </svg>
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-sm text-text-disabled">
            {searchQuery ? `"${searchQuery}"에 해당하는 FAQ가 없어요` : '해당 카테고리의 FAQ가 없어요'}
          </p>
        </div>
      ) : (
        <div className="mt-4 overflow-hidden rounded-xl border border-border bg-surface-raised">
          {filtered.map((faq, i) => (
            <div key={faq.faqId} className={i > 0 ? 'border-t border-border' : ''}>
              <FaqRow
                faq={faq}
                isOpen={openFaqId === faq.faqId}
                onToggle={() => setOpenFaqId(openFaqId === faq.faqId ? null : faq.faqId)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
